import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { PlanFilters } from '../PlanFilters/PlanFilters';
import { PlanCard } from '../PlanCard/PlanCard';
import { useAgentPlans } from '../../hooks/useAgentPlans';
import { usePlanMutations } from '../../hooks/usePlanMutations';
import type { PlanFiltersState, StepContent, RefusalReason } from '../../types/agent.types';

interface PlansViewProps {
  filters: PlanFiltersState;
  onFiltersChange: (partial: Partial<PlanFiltersState>) => void;
}

type SortKey = 'date' | 'affaire' | 'priority' | 'type';

const PRIORITY_ORDER: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

export const PlansView: React.FC<PlansViewProps> = ({ filters, onFiltersChange }) => {
  // Local search state for instant filtering (no API round-trip)
  const [localSearch, setLocalSearch] = useState(filters.search);
  const [sortBy, setSortBy] = useState<SortKey>('date');

  // Fetch plans without search — small dataset, filter client-side
  const apiFilters = useMemo(
    () => ({ ...filters, search: '' }),
    [filters]
  );
  const { plans, isLoading } = useAgentPlans('pending', apiFilters);
  const { reviewStep, validatePlan, retryPlan, isValidating } = usePlanMutations();

  // Client-side search filtering (instant)
  const filteredPlans = useMemo(() => {
    const q = localSearch.trim();
    if (!q) return plans;
    return plans.filter((p) => {
      const snapshot = p.affaire_snapshot || {};
      return (
        String(p.affaire_id).includes(q) ||
        (snapshot.client_name || '').toLowerCase().includes(q.toLowerCase()) ||
        (snapshot.address || '').toLowerCase().includes(q.toLowerCase())
      );
    });
  }, [plans, localSearch]);

  // Restore scroll position when returning from tracker
  useEffect(() => {
    if (isLoading) return;
    const saved = sessionStorage.getItem('agentLeaScrollPosition');
    if (saved) {
      sessionStorage.removeItem('agentLeaScrollPosition');
      const scrollContainer = document.querySelector('.shared-main-content');
      if (scrollContainer) {
        requestAnimationFrame(() => {
          scrollContainer.scrollTop = parseInt(saved, 10);
        });
      }
    }
  }, [isLoading]);

  const handleFiltersChange = useCallback(
    (partial: Partial<PlanFiltersState>) => {
      if (partial.search !== undefined) {
        setLocalSearch(partial.search);
        return;
      }
      onFiltersChange(partial);
    },
    [onFiltersChange]
  );

  const handleReviewStep = useCallback(
    async (planId: string, stepNumber: number, review_status: string, modified_content?: StepContent, refusal_reason?: RefusalReason) => {
      await reviewStep({
        planId,
        stepNumber,
        review_status: review_status as any,
        modified_content,
        refusal_reason,
      });
    },
    [reviewStep]
  );

  const handleValidatePlan = useCallback(
    async (planId: string, comment?: string) => {
      await validatePlan({ planId, comment });
    },
    [validatePlan]
  );

  const handleRetryPlan = useCallback(
    async (planId: string) => {
      await retryPlan(planId);
    },
    [retryPlan]
  );

  // Expose local search to the filter UI
  const filtersWithSearch = useMemo(
    () => ({ ...filters, search: localSearch }),
    [filters, localSearch]
  );

  // Sort plans — must be before any early return to keep hook order stable
  const sortedPlans = useMemo(() => {
    const sorted = [...filteredPlans];
    sorted.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          // Oldest first (first in → top, last in → bottom)
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'affaire':
          return (b.affaire_id || 0) - (a.affaire_id || 0);
        case 'priority':
          return (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9);
        case 'type':
          return (a.plan_type || '').localeCompare(b.plan_type || '');
        default:
          return 0;
      }
    });
    return sorted;
  }, [filteredPlans, sortBy]);

  if (isLoading) {
    return (
      <>
        <PlanFilters filters={filtersWithSearch} onFiltersChange={handleFiltersChange} />
        <div className="agent-loading">
          <span className="agent-spinner agent-spinner--default" />
        </div>
      </>
    );
  }

  const toValidate = sortedPlans.filter((p) => p.status === 'pending');
  const validated = sortedPlans.filter((p) => p.status === 'validated' || p.status === 'executing');

  return (
    <>
      <PlanFilters filters={filtersWithSearch} onFiltersChange={handleFiltersChange} />

      {toValidate.length === 0 && validated.length === 0 ? (
        <div className="agent-empty">
          <div className="agent-empty-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18h6M10 22h4M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z" />
            </svg>
          </div>
          <div className="agent-empty-title">Aucun plan en attente</div>
          <div className="agent-empty-desc">L'agent Lea proposera des plans d'action lorsqu'elle detectera des affaires necessitant une action</div>
        </div>
      ) : (
        <>
          {toValidate.length > 0 && (
            <div className="agent-section">
              <div className="agent-section-header">
                <div className="agent-section-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="12" y1="18" x2="12" y2="12" />
                    <line x1="9" y1="15" x2="15" y2="15" />
                  </svg>
                  A valider
                </div>
                <div className="agent-sort-bar">
                  <span className="agent-sort-label">Trier:</span>
                  {([['priority', 'Priorite'], ['date', 'Date'], ['affaire', 'N° affaire'], ['type', 'Type']] as const).map(([key, label]) => (
                    <button
                      key={key}
                      className={`agent-sort-btn ${sortBy === key ? 'agent-sort-btn--active' : ''}`}
                      onClick={() => setSortBy(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <span className="agent-section-count">{toValidate.length}</span>
              </div>
              {toValidate.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  onReviewStep={handleReviewStep}
                  onValidatePlan={handleValidatePlan}
                  onRetryPlan={handleRetryPlan}
                  isValidating={isValidating}
                />
              ))}
            </div>
          )}

          {validated.length > 0 && (
            <div className="agent-section">
              <div className="agent-section-header">
                <div className="agent-section-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Valides — en attente d'execution
                </div>
                <span className="agent-section-count">{validated.length}</span>
              </div>
              {validated.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  onReviewStep={handleReviewStep}
                  onValidatePlan={handleValidatePlan}
                  onRetryPlan={handleRetryPlan}
                  isValidating={isValidating}
                />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};
